import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Badge, Button, CloseButton, Loader, Menu, Select, Tabs, TextInput } from '@mantine/core'
import {
  IconActivity,
  IconAdjustmentsHorizontal,
  IconAlertTriangle,
  IconChevronDown,
  IconDownload,
  IconFileTypeCsv,
  IconFileTypePdf,
  IconLock,
  IconPencil,
  IconSearch,
  IconShieldCheck,
} from '@tabler/icons-react'
import { formatHumanName } from '@medplum/core'
import { useMedplumProfile } from '@medplum/react'
import type { Patient } from '@medplum/fhirtypes'
import type { AuditAccessEvent } from '@behuman/core-contract'
import { useClient, useOrpc } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'
import { buildAuditCsv, downloadCsv } from './audit-csv'
import { downloadAuditPdf } from './audit-pdf'
import { AccessHistoryTable } from './AccessHistoryTable'
import { ChangeHistoryTab } from './ChangeHistoryTab'
import { AuditFiltersDrawer } from './AuditFiltersDrawer'
import {
  accessQueryFilters,
  applyAccessFilters,
  applyChangeFilters,
  auditScopeNote,
  sectionSlugLabel,
  AUDIT_INTEGRITY_STATEMENTS,
  AUDIT_TAB_LABELS,
  changeQueryFilters,
  buildAuditExportFilename,
  buildClinicOptions,
  buildRoleOptions,
  DATE_RANGE_OPTIONS,
  DEFAULT_AUDIT_FILTERS,
  describeAuditFilters,
  formatAuditDayTime,
  changeRowSubline,
  sectionsForResourceType,
  selectAuditExportRows,
  type AuditCompleteness,
  type AuditFilters,
  type AuditWindow,
  type AuditTab,
} from './auditModel'

type Props = {
  patient: Patient
}

/**
 * PD-1322 — Chart Audit section (decision-independent slice). Layout per the
 * five mockups (Jira attachments 23671-23675): summary cards, Access History /
 * Change History tabs, inline filter bar, and the Audit filters drawer.
 * Data comes from the middleware stub audit endpoints.
 */
export function ChartAudit({ patient }: Props) {
  const patientId = patient.id ?? ''
  const orpc = useOrpc()
  const client = useClient()
  const [filters, setFilters] = useState<AuditFilters>(DEFAULT_AUDIT_FILTERS)
  const [drawerOpen, setDrawerOpen] = useState(false)
  /** The last export attempt could not be recorded, so no file was produced. See runExport. */
  const [exportFailed, setExportFailed] = useState(false)
  /**
   * Each tab's date window, independently: the two lists are capped separately and the
   * server answers each with its own `nextWindowTo`, so one tab paging back must not drag
   * the other with it. Undefined = the most recent window.
   */
  const [accessWindowTo, setAccessWindowTo] = useState<string | undefined>(undefined)
  const [changeWindowTo, setChangeWindowTo] = useState<string | undefined>(undefined)

  /**
   * Any filter change returns both tabs to the most recent window. A `to` carried over from
   * the previous filter set is a boundary the server computed for a different query, so
   * reusing it would silently skip whatever the new filters match above it.
   */
  const applyFilters = (next: AuditFilters) => {
    setFilters(next)
    setAccessWindowTo(undefined)
    setChangeWindowTo(undefined)
  }

  // Section-view reporting happens at the chart frame (useAuditSectionReporting
  // in PatientPage.tsx) for EVERY section — this component no longer
  // self-reports (S8).

  // Every filter the server can express is sent as query input, so the endpoint's page
  // cap applies to the MATCHING events rather than to the newest ones (see
  // accessQueryFilters). Changing a filter refetches.
  const accessQueryInput = useMemo(
    () => ({ patientId, ...accessQueryFilters(filters, accessWindowTo) }),
    [patientId, filters, accessWindowTo],
  )
  const accessQuery = useQuery(
    orpc.auditListAccessEvents.queryOptions({ input: accessQueryInput, enabled: !!patientId }),
  )
  // Change filters ride in the query input too (Greptile P1 on !1336): the server
  // materializes the COMPLETE derived set and applies date/user/role/source/section as
  // SQL predicates, so filtering here over a bounded response can no longer omit
  // matching events. The input is part of the query key, so changing a filter refetches.
  // Section additionally narrows the server's derivation walk (lazy per-section
  // loading, S9); free-text search stays client-side (applyChangeFilters).
  const changeQueryInput = useMemo(
    () => ({ patientId, ...changeQueryFilters(filters, changeWindowTo) }),
    [patientId, filters, changeWindowTo],
  )
  const changeQuery = useQuery(
    orpc.auditListChangeEvents.queryOptions({ input: changeQueryInput, enabled: !!patientId }),
  )

  // The filter VOCABULARY, independent of every filter (Greptile round 2 on !1354).
  // Deriving options from the event responses made them circular: those responses are
  // bounded by the active date range (30 days by default) and by the page cap, so an actor
  // who last touched the chart in March was not offered by the very filter meant to find
  // them, and could only be discovered by widening the range first.
  //
  // GATED ON THE CHANGE QUERY, and that is not incidental (Greptile round 3).
  // `audit_change_event` is a materialization that `auditListChangeEvents` itself populates
  // — the sync runs inside that request. Fetching the options concurrently would race it and
  // could read the table before any change row existed, then cache that empty answer for the
  // session. Waiting for one change response guarantees the sync has run at least once.
  //
  // `syncGeneration` extends that across the sync's CONVERGENCE. The backfill is
  // incremental: a response that reports `syncPending` means more history is still being
  // materialized, so the vocabulary read a moment ago is already stale. Each such response
  // bumps the generation, which is part of the query key, so the options are re-read until
  // the sync settles.
  //
  // KEYED ON `syncPending` SPECIFICALLY, which is why the contract split the flag. The old
  // `truncated` meant "sync unfinished OR page cap hit", and a page cap does not clear by
  // waiting — so on any chart big enough to fill a page, every change response re-read a
  // vocabulary that could not have changed. The poll has to key on the condition that
  // CONVERGES, or it is keyed on something permanent.
  //
  // Not an unbounded loop, despite how it was first reported: the effect's deps are the
  // flag and `dataUpdatedAt`, and the generation bump changes neither. Each options read is
  // paired with a change response, and the effect below bounds how many of those there can
  // be. Measured — a test asserting a spin passes with the conflated flag restored.
  const [syncGeneration, setSyncGeneration] = useState(0)
  /**
   * How many times a pending sync is advanced automatically before the reader is asked.
   *
   * The materialization only advances INSIDE an `auditListChangeEvents` request, so a
   * response that says `syncPending` and is then left alone stays pending forever. The
   * banner told the reader older changes "will appear as they are derived", and nothing
   * derived them — a promise the code did not keep, on the one screen whose job is to be
   * believed.
   *
   * Bounded rather than "until it converges": a very large chart needs tens of requests,
   * each doing real FHIR work, and a component that fires them unattended is the load
   * problem the sync's own concurrency bounds exist to prevent. Ten rounds converges an
   * ordinary chart; past that the reader gets an explicit control and decides.
   */
  const MAX_AUTO_SYNC_ROUNDS = 10
  const [autoSyncRounds, setAutoSyncRounds] = useState(0)
  // The three completeness bits, read once as plain booleans so everything downstream
  // (this effect, the banner, the export selection) keys off stable values.
  const accessCapped = accessQuery.data?.capped === true
  const changeCapped = changeQuery.data?.capped === true
  const changeSyncPending = changeQuery.data?.syncPending === true
  const changeSettledAt = changeQuery.dataUpdatedAt
  useEffect(() => {
    if (changeSyncPending) setSyncGeneration((generation) => generation + 1)
  }, [changeSyncPending, changeSettledAt])

  // Advance the materialization itself, not just the vocabulary read from it.
  //
  // Re-running the change query is the ONLY thing that moves the sync forward — the server
  // does a bounded slice of the backfill inside each request and persists a cursor. Without
  // this the banner's "will appear as they are derived" was false: the tab sat pending until
  // some unrelated interaction happened to refetch.
  //
  // A short delay, so a converging chart does not fire its next round inside the render that
  // handled the last one, and so the reader sees the list grow rather than the tab thrash.
  useEffect(() => {
    if (!changeSyncPending || autoSyncRounds >= MAX_AUTO_SYNC_ROUNDS) return
    const timer = setTimeout(() => {
      setAutoSyncRounds((rounds) => rounds + 1)
      void changeQuery.refetch()
    }, 1_500)
    return () => clearTimeout(timer)
    // `changeSettledAt` is what makes this fire once per RESPONSE rather than once per
    // render: a refetch that comes back still-pending updates it and schedules the next.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeSyncPending, changeSettledAt, autoSyncRounds])

  // A new question means a new convergence budget: the reader has moved to a different
  // window, filter set or patient, and the rounds already spent say nothing about it.
  useEffect(() => {
    setAutoSyncRounds(0)
  }, [patientId, filters, changeWindowTo])

  const autoSyncExhausted = changeSyncPending && autoSyncRounds >= MAX_AUTO_SYNC_ROUNDS

  const filterOptionsQuery = useQuery(
    orpc.auditListFilterOptions.queryOptions({
      input: { patientId },
      enabled: !!patientId && changeQuery.isSuccess,
      queryKey: ['auditListFilterOptions', patientId, syncGeneration],
    }),
  )

  const accessEvents = useMemo(() => accessQuery.data?.events ?? [], [accessQuery.data])
  const changeEvents = useMemo(() => changeQuery.data?.events ?? [], [changeQuery.data])

  const filteredAccess = useMemo(
    () => applyAccessFilters(accessEvents, filters),
    [accessEvents, filters],
  )
  const filteredChanges = useMemo(
    () => applyChangeFilters(changeEvents, filters),
    [changeEvents, filters],
  )

  const [activeTab, setActiveTab] = useState<string | null>('access')
  const profile = useMedplumProfile()
  // The ACTIVE tab's query, because failure is per tab. A failed query used to fall
  // through to the table and render "No access events recorded for the selected filters"
  // — the strongest possible statement about a chart's history, made from having learned
  // nothing. Reading the active tab's own state is what lets one tab say "could not load"
  // while the other goes on listing rows.
  const currentTab: AuditTab = activeTab === 'access' ? 'access' : 'changes'
  const activeQuery = activeTab === 'access' ? accessQuery : changeQuery
  const isLoading = accessQuery.isLoading || changeQuery.isLoading
  const eventsCount = activeTab === 'access' ? filteredAccess.length : filteredChanges.length
  const eventsCapped = activeTab === 'access' ? accessCapped : changeCapped

  /**
   * QA round 3 (re-test) — WHETHER THE CAP STILL DESCRIBES THE COUNT ON SCREEN.
   *
   * Every other filter is pushed down to the endpoint (accessQueryFilters /
   * changeQueryFilters), so the cap the server reports is a cap on the MATCHING events and
   * "there are more" is a fact about them. Free-text search is the one exception: it runs
   * here, over the rows the capped page already returned. With the list capped, typing
   * "Exported" rendered badge "3+", "3+ events" and "Older events match these filters but
   * did not fit in this page" — then "Show older events" returned 0. Exactly three matched
   * in total. The `+` and that sentence were the server's claim about ITS filters, worn by
   * a number the server never computed.
   *
   * So the cap stops decorating the count the moment a search is active, and the banner
   * says what is actually known — that a page boundary exists and the events beyond it have
   * not been searched. The banner itself stays, because the cap is still real and "Show
   * older events" is still how a reader reaches the rest.
   */
  const searchNarrowed = filters.search.trim() !== ''

  // QA round 1 (W5) — the filter option vocabularies must not narrow themselves.
  // They derive from the returned events, and the filters are pushed down to the
  // server, so picking a user used to shrink the next response to that user alone —
  // and with it the option list the pick came from: every other user vanished until
  // reset. The vocabulary therefore ACCUMULATES every value seen for this patient
  // over the component's lifetime (a ref that only grows, reset on patient change),
  // so a narrowed response can never remove an option that was offerable a moment ago.
  const seenRef = useRef({
    patientId: '',
    users: new Set<string>(),
    sections: new Set<string>(),
    roles: new Set<string>(),
    /** Clinic reference -> display label (see buildClinicOptions for the split). */
    clinics: new Map<string, string>(),
  })
  const filterOptions = filterOptionsQuery.data
  const { userOptions, clinicOptions, sectionOptions, roleOptions } = useMemo(() => {
    const vocabulary = seenRef.current
    if (vocabulary.patientId !== patientId) {
      vocabulary.patientId = patientId
      vocabulary.users.clear()
      vocabulary.sections.clear()
      vocabulary.roles.clear()
      vocabulary.clinics.clear()
    }
    for (const event of accessEvents) {
      vocabulary.users.add(event.actorName)
      if (event.actorRole) vocabulary.roles.add(event.actorRole)
      for (const section of event.sections) vocabulary.sections.add(section)
      if (event.actorOrganizationReference) {
        vocabulary.clinics.set(
          event.actorOrganizationReference,
          event.actorOrganization ?? event.actorOrganizationReference,
        )
      }
    }
    // Greptile on !1354 — CHANGE events contribute to the same vocabularies. The user and
    // section selects are shared by both tabs and push their value to both queries, so
    // building them from access events alone made a whole dimension of Change History
    // unreachable: an actor who edited the chart without opening it (an integration, a
    // background job, a clinician editing from elsewhere) was never offered, and neither
    // was a section that has changes but no recorded section views. Clinics are not
    // accumulated here because a change event carries no organization.
    for (const event of changeEvents) {
      if (event.actorName) vocabulary.users.add(event.actorName)
      if (event.actorRole) vocabulary.roles.add(event.actorRole)
      for (const section of sectionsForResourceType(event.resourceType)) {
        vocabulary.sections.add(section)
      }
    }
    // The authoritative set, unbounded by any filter. Merged rather than used alone so the
    // options stay right while it is still loading, and so an event that arrives before it
    // does is never missing from the list.
    for (const user of filterOptions?.users ?? []) vocabulary.users.add(user)
    for (const section of filterOptions?.sections ?? []) vocabulary.sections.add(section)
    for (const role of filterOptions?.roles ?? []) vocabulary.roles.add(role)
    for (const clinic of filterOptions?.clinics ?? []) {
      vocabulary.clinics.set(clinic.reference, clinic.name)
    }
    return {
      userOptions: [
        { value: 'all', label: 'All users' },
        ...[...vocabulary.users].map((name) => ({ value: name, label: name })),
      ],
      // AC8's clinic dimension — same accumulated vocabulary as the users above.
      clinicOptions: buildClinicOptions(vocabulary.clinics),
      // QA round 3 (re-test) — roles come from the record, not from a hardcoded list that
      // append-only history guarantees will be incomplete. See buildRoleOptions.
      roleOptions: buildRoleOptions([...vocabulary.roles]),
      sectionOptions: [
        { value: 'all', label: 'All sections' },
        ...[...vocabulary.sections].map((section) => ({
          value: section,
          label: sectionSlugLabel(section),
        })),
      ],
    }
  }, [accessEvents, changeEvents, filterOptions, patientId])

  // ONE decision about what an export carries, consumed by BOTH formats: the CSV and the
  // PDF serialize this same selection, so they cannot disagree about the filtered set (and,
  // because it holds the arrays the tables render, neither can disagree with the screen).
  // What each tab's response said about its own completeness. Access events are never
  // materialized, so nothing can be pending for them — `syncPending: false` is a fact about
  // that endpoint, not a placeholder.
  const accessCompleteness = useMemo<AuditCompleteness>(
    () => ({ syncPending: false, capped: accessCapped }),
    [accessCapped],
  )
  const changeCompleteness = useMemo<AuditCompleteness>(
    () => ({ syncPending: changeSyncPending, capped: changeCapped }),
    [changeSyncPending, changeCapped],
  )
  const currentCompleteness = currentTab === 'access' ? accessCompleteness : changeCompleteness
  const currentWindow: AuditWindow =
    currentTab === 'access'
      ? { to: accessWindowTo, nextTo: accessQuery.data?.nextWindowTo }
      : { to: changeWindowTo, nextTo: changeQuery.data?.nextWindowTo }
  const setCurrentWindowTo = currentTab === 'access' ? setAccessWindowTo : setChangeWindowTo
  // QA round 3 — what the summary cards may claim about their own scope (see below).
  const scopeNote = auditScopeNote(filters)

  const exportSelection = useMemo(
    () =>
      selectAuditExportRows({
        tab: currentTab,
        filteredAccess,
        filteredChanges,
        access: accessCompleteness,
        changes: changeCompleteness,
      }),
    [currentTab, filteredAccess, filteredChanges, accessCompleteness, changeCompleteness],
  )

  // Attribution for the PDF header. A compliance export read months later has to name the
  // patient and the operator; 'Unknown user' rather than a blank, so a gap reads as a gap.
  const patientLabel = patient.name?.[0]
    ? `${formatHumanName(patient.name[0])} (${patientId})`
    : patientId
  const generatedBy = profile?.name?.[0] ? formatHumanName(profile.name[0]) : 'Unknown user'

  /**
   * S11/S13 — one export path for both formats. The artefact leaves the building either
   * way, so either way it is itself an audited access: one report per click, the server
   * authors the record, and the refetch makes it show up in Access History right away. The
   * ACTION distinguishes the two so a compliance reader can tell which file was produced.
   *
   * THE RECORD IS WRITTEN FIRST, AND A FAILURE TO WRITE IT ABORTS THE EXPORT. This is a
   * deliberate behaviour change: the file used to download regardless, with the failed
   * report swallowed into a log line, so a chart export could leave the building with
   * nothing in the audit trail saying it had. For a HIPAA disclosure record an export that
   * failed is a smaller problem than an export nobody can find — the reader sees the
   * failure and can retry, where an unrecorded disclosure is invisible forever.
   *
   * The residual case is the reverse: the record is written and the file then fails to
   * generate, leaving a disclosure logged that did not happen. That is the direction to
   * err in — a false entry in the log is visible and explicable, a missing one is not.
   */
  const runExport = async (format: 'csv' | 'pdf') => {
    setExportFailed(false)
    try {
      await client.auditReportAction({
        patientId,
        action: format === 'pdf' ? 'audit-export-pdf' : 'audit-export',
      })
    } catch (err) {
      logError('patient', `Failed to record audit ${format.toUpperCase()} export`, err)
      setExportFailed(true)
      return
    }

    const filename = buildAuditExportFilename(exportSelection.tab, patientId, format)
    if (format === 'csv') {
      downloadCsv(buildAuditCsv(exportSelection), filename)
    } else {
      downloadAuditPdf(
        exportSelection,
        {
          patientLabel,
          generatedBy,
          generatedAt: new Date().toISOString(),
          // The tab is what decides which dimensions actually selected these rows —
          // half of them are access-only. See describeAuditFilters.
          filters: describeAuditFilters(filters, clinicOptions, roleOptions, exportSelection.tab),
        },
        filename,
      )
    }
    void accessQuery.refetch()
  }

  return (
    <div className="mx-auto max-w-[1400px] px-8 py-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-teal-700 uppercase">
            <IconShieldCheck size={14} />
            Security & compliance
          </div>
          <h1 className="mt-1 text-2xl font-bold text-gray-900">Chart Audit</h1>
          <p className="mt-1 text-sm text-gray-500">
            Review who accessed this chart, when, and what was viewed or changed.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="default"
            leftSection={<IconAdjustmentsHorizontal size={16} />}
            onClick={() => setDrawerOpen(true)}
          >
            More filters
          </Button>
          {/* CSV or PDF export of the filtered current tab (S11). Ungated per Jira
              comment 24733. */}
          <Menu shadow="md" position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                color="teal"
                leftSection={<IconDownload size={16} />}
                rightSection={<IconChevronDown size={14} />}
                // Nothing to export from a tab that failed to load: the selection would be
                // the empty array, and an empty compliance artefact asserts that no event
                // matched rather than that no answer arrived.
                disabled={activeQuery.isError}
              >
                Export audit
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconFileTypeCsv size={16} />}
                onClick={() => void runExport('csv')}
              >
                Export as CSV
              </Menu.Item>
              <Menu.Item
                leftSection={<IconFileTypePdf size={16} />}
                onClick={() => void runExport('pdf')}
              >
                Export as PDF
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </div>

      {/* An aborted export has to be visible. It is the one case where the reader asked
          for a file and no file arrived, and silence would read as a browser problem. */}
      {exportFailed && (
        <div
          role="alert"
          data-testid="audit-export-failed"
          className="mt-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900"
        >
          No file was produced. This export could not be recorded in the audit trail, and an
          unrecorded export of a patient chart is not permitted — nothing was downloaded. Try again,
          and report it if it keeps failing.
        </div>
      )}

      {/* Summary cards. Active-time and access-status cards were cut from the
          MVP (Alex, 2026-07-30) — only Last accessed / Last modified remain.

          QA round 3 — the cards derive from the CURRENT view (the queries are
          server-filtered), so their labels carry the view's scope: "Last accessed
          (last 30 days)" or "(filtered view)". A bare label appears only when the view
          spans the whole record, which is the only state where the card is a
          chart-level fact.

          QA round 3 (re-test) — FROM THE RENDERED ROWS, not from the raw responses.
          Free-text search is the one filter with no server-side counterpart, so it is
          applied here (applyAccessFilters / applyChangeFilters) and the responses still
          hold everything it excluded. Reading the responses therefore let the card claim
          "Last accessed (filtered view) — Today, 1:54 AM" directly above a table saying
          "No access events recorded for the selected filters": a timestamp no visible row
          supported. `filteredAccess` / `filteredChanges` are exactly the arrays the tables
          render, so the card and the table can no longer disagree. */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        {/* An em dash means "never" — only claimable on an unnarrowed view; a scoped
            view that is empty says so instead. A failed query claims nothing at all. */}
        <SummaryCard
          icon={<IconActivity size={16} className="text-indigo-500" />}
          label={scopeNote ? `Last accessed (${scopeNote})` : 'Last accessed'}
          value={
            accessQuery.isError
              ? 'Unavailable'
              : (latestAccessValue(filteredAccess) ?? (scopeNote ? 'None in view' : '—'))
          }
          sub={accessQuery.isError ? undefined : filteredAccess[0]?.actorName}
        />
        <SummaryCard
          icon={<IconPencil size={16} className="text-teal-600" />}
          label={scopeNote ? `Last modified (${scopeNote})` : 'Last modified'}
          value={
            changeQuery.isError
              ? 'Unavailable'
              : filteredChanges[0]
                ? formatAuditDayTime(filteredChanges[0].recordedAt)
                : scopeNote
                  ? 'None in view'
                  : '—'
          }
          sub={
            !changeQuery.isError && filteredChanges[0]
              ? changeRowSubline(filteredChanges[0])
              : undefined
          }
        />
      </div>

      {/* Tabs */}
      <div className="mt-6 rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-4">
          <Tabs value={activeTab} onChange={setActiveTab} color="teal">
            <Tabs.List className="border-b-0">
              {/* A count of 0 on a failed query is a claim about the record. Dash out.
                  QA round 3 — the badges are NOT `circle`: a circle Badge is
                  fixed-width and clips any count past one glyph ("1..." for 14). The
                  pill grows with the number; counts are 2-4 digits on real charts.

                  QA round 3 (re-test) — a capped page renders as "998+", never as "998".
                  See eventCountLabel. */}
              <Tabs.Tab
                value="access"
                rightSection={
                  <Badge
                    size="sm"
                    variant="light"
                    color="teal"
                    title={countTitle(accessCapped, searchNarrowed)}
                  >
                    {accessQuery.isError
                      ? '—'
                      : eventCountLabel(filteredAccess.length, accessCapped, searchNarrowed)}
                  </Badge>
                }
              >
                Access History
              </Tabs.Tab>
              <Tabs.Tab
                value="changes"
                rightSection={
                  <Badge
                    size="sm"
                    variant="light"
                    color="teal"
                    title={countTitle(changeCapped, searchNarrowed)}
                  >
                    {changeQuery.isError
                      ? '—'
                      : eventCountLabel(filteredChanges.length, changeCapped, searchNarrowed)}
                  </Badge>
                }
              >
                Change History
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>
          {/* Per tab: only the ACCESS table is append-only in the database. See
              AUDIT_INTEGRITY_STATEMENTS. */}
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <IconLock size={12} />
            {AUDIT_INTEGRITY_STATEMENTS[currentTab]}
          </div>
        </div>

        {/* Inline filter bar.
            QA round 3 (re-test) — WRAPS. Five fixed-width controls on one nowrap row were
            squeezed together below ~1000px until the selects showed "Encount…" and
            "History a… Physical", so the reader could not tell which filter was in force.
            Wrapping keeps every control at a width its longest option fits in; the widest
            section label ("History and Physical") is what sets the 12rem selects. */}
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3">
          <TextInput
            className="w-72 max-w-full"
            size="sm"
            placeholder="Search team member or activity"
            leftSection={<IconSearch size={14} />}
            // QA round 1 (W4) — clearable: one click empties the search on either tab
            // (this input filters whichever tab is active).
            rightSection={
              filters.search !== '' ? (
                <CloseButton
                  size="sm"
                  aria-label="Clear search"
                  onClick={() => applyFilters({ ...filters, search: '' })}
                />
              ) : undefined
            }
            value={filters.search}
            onChange={(event) => applyFilters({ ...filters, search: event.currentTarget.value })}
          />
          <Select
            size="sm"
            className="w-48"
            data={DATE_RANGE_OPTIONS}
            value={filters.dateRange}
            onChange={(value) => value && applyFilters({ ...filters, dateRange: value })}
            allowDeselect={false}
            aria-label="Date range"
          />
          <Select
            size="sm"
            className="w-48"
            data={userOptions}
            value={filters.user}
            onChange={(value) => value && applyFilters({ ...filters, user: value })}
            allowDeselect={false}
            aria-label="User"
          />
          <Select
            size="sm"
            className="w-48"
            data={sectionOptions}
            value={filters.section}
            onChange={(value) => value && applyFilters({ ...filters, section: value })}
            allowDeselect={false}
            aria-label="Section"
          />
          {/* QA round 1 (W2) — shrink-0 + nowrap: the counter sits last in a flex row
              of fixed-width inputs, so it was the element flex shrank and clipped. */}
          <div
            className="ml-auto shrink-0 text-xs whitespace-nowrap text-gray-400"
            title={countTitle(eventsCapped, searchNarrowed)}
          >
            {activeQuery.isError
              ? '—'
              : `${eventCountLabel(eventsCount, eventsCapped, searchNarrowed)} event${
                  eventsCount === 1 && (searchNarrowed || !eventsCapped) ? '' : 's'
                }`}
          </div>
        </div>

        {/* ONE block, both statements, sync before cap.
            They are unrelated and can be true together, and they ask different things of
            the reader: a derivation still running will fill the gap by itself, a capped
            page never will. Two separate banners would stack and read as one problem
            stated twice; one sentence for both — which the old conflated flag forced —
            told a reader to narrow their filters when the answer was "wait". */}
        {(currentCompleteness.syncPending ||
          currentCompleteness.capped ||
          currentWindow.to !== undefined) && (
          <div
            role="status"
            data-testid="audit-completeness-banner"
            className="mx-4 mt-4 space-y-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          >
            {currentCompleteness.syncPending && (
              <div data-testid="audit-sync-pending">
                <p>
                  Still reading this chart&apos;s older records. This list is not yet the complete
                  change history.
                </p>
                {/* Only a change-events request advances the derivation, so the promise
                    "they will appear" has to be backed by one. Above the automatic budget
                    the reader is asked rather than told, because each round is real work on
                    the FHIR server and an unattended tab should not spend it forever. */}
                {autoSyncExhausted ? (
                  <Button
                    size="compact-xs"
                    variant="light"
                    color="amber"
                    className="mt-1"
                    onClick={() => {
                      setAutoSyncRounds(0)
                      void changeQuery.refetch()
                    }}
                  >
                    Keep reading older records
                  </Button>
                ) : (
                  <p className="mt-0.5 text-xs">
                    Older changes are being derived now and will appear here on their own.
                  </p>
                )}
              </div>
            )}
            {currentCompleteness.capped && (
              <div data-testid="audit-capped">
                <p>
                  Showing {eventsCount.toLocaleString()} matching events
                  {currentWindow.to
                    ? ` recorded before ${formatAuditDayTime(currentWindow.to)}`
                    : ', the most recent'}
                  .{' '}
                  {/* Only the SECOND sentence turns on the search, because only it makes a
                      claim about the events beyond the page boundary. Those were matched
                      against the SERVER's filters and never against the search box, so
                      "Older events match these filters" asserts something this client has
                      not looked at — and did: three matches, badge "3+", and "Show older
                      events" then returning nothing. See `searchNarrowed`. */}
                  {searchNarrowed
                    ? 'Older events did not fit in this page and have not been searched.'
                    : 'Older events match these filters but did not fit in this page.'}
                </p>
                {/* The window can only advance to the timestamp of a COMPLETE group. When
                    the whole page IS one group the server has no such timestamp to offer,
                    and paging would either skip events or return the same ones forever —
                    so there is no control, and the reader is told what to do instead.
                    Absence of `nextTo` is NEVER "that is the end of the record". */}
                {currentWindow.nextTo ? (
                  <Button
                    variant="default"
                    size="xs"
                    className="mt-2"
                    onClick={() => setCurrentWindowTo(currentWindow.nextTo)}
                  >
                    Show older events
                  </Button>
                ) : (
                  <p className="mt-1" data-testid="audit-window-stuck">
                    Every event in this page shares one timestamp, so the window cannot move past
                    it. Narrow your filters to reach the rest — this is not the end of the record.
                  </p>
                )}
              </div>
            )}
            {/* Shown whether or not this window is itself capped: once the reader has paged
                back, the list is no longer "the most recent" and nothing else says so. */}
            {currentWindow.to && (
              <p data-testid="audit-window-active">
                <Button
                  variant="subtle"
                  size="xs"
                  className="px-0"
                  onClick={() => setCurrentWindowTo(undefined)}
                >
                  Back to the most recent events
                </Button>
              </p>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
            <Loader size="sm" /> Loading audit events...
          </div>
        ) : activeQuery.isError ? (
          <AuditLoadFailure
            error={activeQuery.error}
            tab={currentTab}
            onRetry={() => void activeQuery.refetch()}
          />
        ) : activeTab === 'access' ? (
          <AccessHistoryTable events={filteredAccess} />
        ) : (
          <ChangeHistoryTab events={filteredChanges} />
        )}
      </div>

      <AuditFiltersDrawer
        opened={drawerOpen}
        filters={filters}
        clinicOptions={clinicOptions}
        roleOptions={roleOptions}
        onClose={() => setDrawerOpen(false)}
        onApply={(next) => {
          applyFilters(next)
          setDrawerOpen(false)
        }}
      />
    </div>
  )
}

/**
 * Whether the request was REFUSED rather than failed.
 *
 * Read duck-typed on `status`, following `doseSpotLaunchFailure.ts`: oRPC's own type
 * definition warns that `instanceof ORPCError` does not survive bundle boundaries, and a
 * hook in this package already hit that. Anything unrecognised falls through to the
 * generic failure, never to the refusal — a refusal is a statement about the reader's
 * permissions and must not be invented from a network error.
 */
function isAccessRefused(error: unknown): boolean {
  return (
    error != null && typeof error === 'object' && (error as { status?: unknown }).status === 403
  )
}

/**
 * What the tab renders when its query did not answer.
 *
 * The whole point is that this is NOT the empty table. "No access events recorded for the
 * selected filters" is a finding about the chart; a request that failed produced no
 * finding, and on an audit trail the difference is the difference between "nobody opened
 * this record" and "we do not know who opened this record".
 */
function AuditLoadFailure({
  error,
  tab,
  onRetry,
}: {
  error: unknown
  tab: AuditTab
  onRetry: () => void
}) {
  const refused = isAccessRefused(error)
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-3 px-6 py-12 text-center"
      data-testid="audit-load-failure"
    >
      <IconAlertTriangle size={24} className="text-amber-500" />
      <div className="text-sm font-semibold text-gray-900">
        {refused
          ? `You do not have permission to view this chart's ${AUDIT_TAB_LABELS[tab]}.`
          : `${AUDIT_TAB_LABELS[tab]} could not be loaded.`}
      </div>
      <div className="max-w-lg text-sm text-gray-500">
        {refused
          ? 'Audit records are restricted to staff accounts. This is a permissions result, not an empty record — events may exist that you cannot see.'
          : 'The request did not complete, so this tab is showing nothing rather than nothing having happened. Try again, and report it if it keeps failing.'}
      </div>
      {!refused && (
        <Button variant="default" size="xs" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  )
}

type SummaryCardProps = {
  icon: React.ReactNode
  label: string
  value: string
  sub?: React.ReactNode
}

function SummaryCard({ icon, label, value, sub }: SummaryCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-gray-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-500">{sub}</div>}
    </div>
  )
}

/**
 * QA round 3 (re-test) — a COUNT, or a lower bound when the response was capped.
 *
 * The badge and the inline counter render the length of the rows in hand, and on a capped
 * page that length is the endpoint's page size, not a total: unfiltered read "998 events"
 * and narrowing to one role read "999", so a subset appeared larger than the whole it came
 * from. Both numbers were the cap. A total nobody measured must not be presented as one —
 * "998+" says what is actually known, and matches the banner below it, which has always
 * been honest ("Showing N matching events, the most recent…").
 *
 * QA round 3 (re-test) — and the `+` comes OFF again under a free-text search. It used to be
 * justified as "a true lower bound even when the client-side search has narrowed the capped
 * page further", which is technically true and read as a promise of more: "3+" beside three
 * rows, when three was the whole answer and paging back produced nothing. The cap is a fact
 * about the server's filters; the search is applied here, afterwards, so the count under a
 * search is a count of THIS PAGE and the banner is what scopes it. See `searchNarrowed`.
 */
function eventCountLabel(count: number, capped: boolean, searched: boolean): string {
  return capped && !searched ? `${count.toLocaleString()}+` : count.toLocaleString()
}

/** The `+`, or its absence under a search, spelled out for the reader. */
function countTitle(capped: boolean, searched: boolean): string | undefined {
  if (!capped) return undefined
  return searched
    ? 'Counted within this page. Older events did not fit in it and have not been searched.'
    : 'At least this many events match. Older matching events did not fit in this page.'
}

/** Undefined when the view holds nothing — the caller decides what "empty" may claim. */
function latestAccessValue(events: AuditAccessEvent[]): string | undefined {
  return events[0] ? formatAuditDayTime(events[0].recordedAt) : undefined
}
